import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chatbotService } from '../../../services/ChatbotService';
import type {
  ChatbotConfigUpdate,
  ChatbotWithIdentity,
} from '../../../types/chatbot';
import { chatbotQueryKeys } from '../chatbotQueryKeys';

export function useCreateChatbotMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { knowledgeBaseId: string; name: string }) =>
      chatbotService.createChatbot(input),
    onSuccess: (chatbot) => {
      void queryClient.invalidateQueries({ queryKey: chatbotQueryKeys.list() });
      if (chatbot.id) {
        void queryClient.invalidateQueries({
          queryKey: chatbotQueryKeys.chatbot(chatbot.id),
        });
      }
    },
  });
}

export function useUpdateChatbotMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      chatbot: ChatbotWithIdentity;
      updates: ChatbotConfigUpdate;
    }) => chatbotService.updateChatbot(input.chatbot, input.updates),
    onSuccess: (chatbot) => {
      void queryClient.invalidateQueries({ queryKey: chatbotQueryKeys.list() });
      void queryClient.invalidateQueries({
        queryKey: chatbotQueryKeys.chatbot(chatbot.id),
      });
    },
  });
}

export function useUpdateChatbotLogoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      chatbotId: string;
      file?: File | null;
      remove?: boolean;
    }) => {
      if (input.file) {
        return chatbotService.uploadChatbotLogo(input.chatbotId, input.file);
      }
      if (input.remove) {
        await chatbotService.deleteChatbotLogo(input.chatbotId);
        return chatbotService.getChatbot(input.chatbotId);
      }
      throw new Error('No logo change requested');
    },
    onSuccess: (chatbot) => {
      void queryClient.invalidateQueries({ queryKey: chatbotQueryKeys.list() });
      void queryClient.invalidateQueries({
        queryKey: chatbotQueryKeys.chatbot(chatbot.id),
      });
    },
  });
}

export function useDeleteChatbotMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatbotId: string) => chatbotService.deleteChatbot(chatbotId),
    onSuccess: (_result, chatbotId) => {
      void queryClient.invalidateQueries({ queryKey: chatbotQueryKeys.list() });
      void queryClient.removeQueries({
        queryKey: chatbotQueryKeys.chatbot(chatbotId),
      });
    },
  });
}
